import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { TitleWithHelp, HelpTooltip } from '../components/ui/help-tooltip';
import {
  Plus, Search, FileText, DollarSign, Eye, Edit, Trash2, Download,
  Ship, Package, Wrench, ChevronDown, X, Loader2,
  Send, CheckCircle2, Layers, CalendarIcon, ExternalLink, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { API, getAuthHeader, formatCurrency, formatDate, quillModules, quillFormats } from '../components/CRM/crmUtils';
import { STATUS_CONFIG, SubItensCatalogo, EmbarcacaoItemEditor, PropostaViewModal } from '../components/CRM/Propostas';
import CadastroRapidoProdutoDialog from '../components/CadastroRapidoProdutoDialog';
import { CurrencyInput } from '../components/ui/currency-input';

// ============ MAIN COMPONENT ============
export default function Propostas() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [propostas, setPropostas] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [busca, setBusca] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProposta, setEditingProposta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('cliente');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewProposta, setViewProposta] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null); // {url, titulo, blobUrl}

  const [clienteSearchTerm, setClienteSearchTerm] = useState('');
  const [clienteSearchResults, setClienteSearchResults] = useState([]);
  const [searchingCliente, setSearchingCliente] = useState(false);

  const [form, setForm] = useState({
    oportunidade_id: '', cliente_id: '', cliente_nome: '', cliente_cnpj: '', cliente_email: '', cliente_telefone: '',
    // Endereço principal do cliente
    cliente_cep: '', cliente_endereco: '', cliente_numero: '', cliente_complemento: '',
    cliente_bairro: '', cliente_cidade: '', cliente_estado: '',
    // Endereço de entrega (estruturado)
    entrega_usar_principal: true,
    entrega_cep: '', entrega_endereco: '', entrega_numero: '', entrega_complemento: '',
    entrega_bairro: '', entrega_cidade: '', entrega_estado: '',
    // Legacy (mantido para PDF/retrocompat — preenchido automaticamente)
    endereco_entrega: '',
    empresa_id: '', empresa_nome: '',
    itens: [],
    desconto_tipo: 'percentual', desconto_valor: 0,
    condicoes: { forma_pagamento: '', prazo_entrega: '', validade_dias: 30, garantia: '', frete: 'FOB', observacoes: '' },
    texto_apresentacao: '', texto_final: '',
    exibir_subtotal_lote: false,
  });

  const [modelos, setModelos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [oportunidades, setOportunidades] = useState([]);
  const [buscaProduto, setBuscaProduto] = useState('');
  const [produtoResults, setProdutoResults] = useState([]);
  const [searchingProd, setSearchingProd] = useState(false);
  const [tiposServico, setTiposServico] = useState([]);
  const [showCadastroRapidoProduto, setShowCadastroRapidoProduto] = useState(false);
  const [categoriasLookup, setCategoriasLookup] = useState([]);
  const [marcasLookup, setMarcasLookup] = useState([]);

  // ============ FETCH ============
  const fetchPropostas = useCallback(async () => {
    try {
      setLoading(true);
      const [propRes, statsRes] = await Promise.all([
        api.get('/propostas'),
        api.get('/propostas/stats'),
      ]);
      setPropostas(propRes.data);
      setStats(statsRes.data);
    } catch { toast.error('Erro ao carregar propostas'); }
    finally { setLoading(false); }
  }, []);

  const fetchLookups = useCallback(async () => {
    try {
      const [modelosRes, empresasRes, oportRes, tiposRes, produtosRes] = await Promise.all([
        api.get('/propostas/buscar/modelos'),
        api.get('/empresas').catch(() => ({ data: { items: [] } })),
        api.get('/crm-oportunidades').catch(() => ({ data: [] })),
        api.get('/tipos-servico').catch(() => ({ data: { items: [] } })),
        api.get('/produtos').catch(() => ({ data: { items: [] } })),
      ]);
      setModelos(modelosRes.data);
      setEmpresas(empresasRes.data?.items || []);
      const opData = oportRes.data || [];
      setOportunidades(Array.isArray(opData) ? opData : []);
      setTiposServico(tiposRes.data?.items || []);
      const prods = produtosRes.data?.items || produtosRes.data || [];
      const cats = new Set(); const mrs = new Set();
      prods.forEach(p => { if (p.categoria) cats.add(p.categoria); if (p.marca) mrs.add(p.marca); });
      setCategoriasLookup([...cats].sort());
      setMarcasLookup([...mrs].sort());
    } catch (err) { console.error('Erro lookups:', err); }
  }, []);

  useEffect(() => { fetchPropostas(); fetchLookups(); }, [fetchPropostas, fetchLookups]);

  useEffect(() => {
    const opId = searchParams.get('oportunidade');
    if (opId && oportunidades.length > 0 && !modalOpen) {
      openNew();
      setTimeout(() => vincularOportunidade(opId), 100);
      setSearchParams({});
    }
  }, [searchParams, oportunidades]); // eslint-disable-line

  // ============ CLIENT SEARCH ============
  const buscarClientes = useCallback(async (termo) => {
    if (!termo || termo.length < 2) { setClienteSearchResults([]); return; }
    try {
      setSearchingCliente(true);
      const res = await api.get(`/crm-oportunidades/buscar-clientes?q=${encodeURIComponent(termo)}`);
      setClienteSearchResults(res.data || []);
    } catch { setClienteSearchResults([]); }
    finally { setSearchingCliente(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => buscarClientes(clienteSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [clienteSearchTerm, buscarClientes]);

  const handleSelectCliente = (cliente) => {
    let cnpj = cliente.cnpj || '';
    let nome = cliente.nome || '';
    // Fallback: if cnpj is empty, check if nome contains a CNPJ
    if (!cnpj && nome) {
      const cnpjMatch = nome.match(/\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}/);
      if (cnpjMatch) {
        cnpj = cnpjMatch[0];
        // If nome IS just a CNPJ, use razao_social as name
        if (nome.trim() === cnpjMatch[0]) {
          nome = cliente.razao_social || nome;
        }
      }
    }
    setForm(f => ({
      ...f,
      cliente_id: cliente.id,
      cliente_nome: nome,
      cliente_cnpj: cnpj || cliente.cpf || '',
      cliente_email: cliente.email || '',
      cliente_telefone: cliente.telefone || '',
      // Endereço principal do cliente
      cliente_cep: cliente.cep || '',
      cliente_endereco: cliente.endereco || '',
      cliente_numero: cliente.numero || '',
      cliente_complemento: cliente.complemento || '',
      cliente_bairro: cliente.bairro || '',
      cliente_cidade: cliente.cidade || '',
      cliente_estado: cliente.estado || '',
    }));
    setClienteSearchTerm('');
    setClienteSearchResults([]);
  };

  // ============ PRODUCT SEARCH ============
  const searchProdutos = useCallback(async (q) => {
    if (!q || q.length < 2) { setProdutoResults([]); return; }
    try {
      setSearchingProd(true);
      const res = await api.get(`/propostas/buscar/produtos?q=${encodeURIComponent(q)}`);
      setProdutoResults(res.data);
    } catch { setProdutoResults([]); }
    finally { setSearchingProd(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchProdutos(buscaProduto), 300);
    return () => clearTimeout(timer);
  }, [buscaProduto, searchProdutos]);

  // ============ CALC TOTALS ============
  const calcSubtotal = (item) => {
    if (item.tipo === 'embarcacao') {
      const base = (item.preco_base || 0) * (item.quantidade || 1);
      const upgrades = (item.upgrades_selecionados || []).reduce((s, u) => s + (u.preco_adicional || 0), 0) * (item.quantidade || 1);
      const opcionais = (item.opcionais_selecionados || []).reduce((s, o) => s + (o.preco || 0), 0) * (item.quantidade || 1);
      return base + upgrades + opcionais;
    }
    if (item.tipo === 'composicao') {
      if (item.preco_por_subitem) {
        return (item.sub_itens || []).reduce((s, si) => s + ((si.preco_unitario || 0) * (si.quantidade || 1)), 0) * (item.quantidade || 1);
      }
      return (item.preco_unitario || 0) * (item.quantidade || 1);
    }
    return (item.preco_unitario || 0) * (item.quantidade || 1);
  };

  const totalItens = form.itens.reduce((sum, item) => sum + calcSubtotal(item), 0);
  const descontoCalc = form.desconto_tipo === 'percentual' ? totalItens * (form.desconto_valor / 100) : form.desconto_valor;
  const totalFinal = totalItens - descontoCalc;

  // ============ ADD ITEMS ============
  const addEmbarcacao = (modelo) => {
    const config = modelo.config_comercial || {};
    setForm(f => ({
      ...f,
      itens: [...f.itens, {
        id: crypto.randomUUID(), tipo: 'embarcacao', ordem: f.itens.length + 1,
        vessel_model_id: modelo.id, vessel_model_nome: modelo.nome, vessel_model_codigo: modelo.codigo,
        quantidade: 1, preco_base: config.preco_base || 0,
        itens_serie: config.itens_serie || [],
        upgrades_selecionados: [], opcionais_selecionados: [],
        _available_upgrades: config.upgrades || [], _available_opcionais: config.opcionais || [],
        subtotal: config.preco_base || 0,
      }],
      texto_apresentacao: f.texto_apresentacao || config.texto_apresentacao || '',
    }));
    setActiveTab('itens');
  };

  const addProduto = (produto) => {
    // Converte descrição técnica do cadastro (texto puro) em HTML simples para o ReactQuill
    // Preserva quebras de linha. O usuário pode editar livremente sem afetar o cadastro principal.
    const descToHtml = (txt) => {
      if (!txt || !String(txt).trim()) return '';
      const safe = String(txt)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const paragrafos = safe.split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`);
      return paragrafos.join('');
    };
    const specsFonte = (produto.descricao_completa && String(produto.descricao_completa).trim())
      || (produto.descricao && String(produto.descricao).trim())
      || '';
    const specsIniciais = descToHtml(specsFonte);

    // Garante valor numérico (alguns produtos vêm com preco_venda como string ou null)
    const precoNum = Number(produto.preco_venda);
    const precoFinal = Number.isFinite(precoNum) && precoNum > 0 ? precoNum : 0;

    setForm(f => ({
      ...f,
      itens: [...f.itens, {
        id: crypto.randomUUID(), tipo: 'produto', ordem: f.itens.length + 1,
        produto_id: produto.id, produto_nome: produto.nome, produto_sku: produto.sku || '',
        quantidade: 1, preco_unitario: precoFinal, subtotal: precoFinal,
        specs_tecnicas: specsIniciais,
      }]
    }));
    setBuscaProduto(''); setProdutoResults([]);
    if (specsIniciais) {
      toast.success('Especificação técnica do cadastro vinculada — edite à vontade nesta proposta.', { duration: 4500 });
    }
    if (precoFinal === 0) {
      toast.warning('Atenção: este produto não tem preço de venda cadastrado. Defina o preço unitário manualmente.', { duration: 5000 });
    }
  };

  const addServico = (tipoServico) => {
    setForm(f => ({
      ...f,
      itens: [...f.itens, {
        id: crypto.randomUUID(), tipo: 'servico', ordem: f.itens.length + 1,
        descricao: tipoServico ? tipoServico.nome : '',
        tipo_servico_id: tipoServico?.id || '',
        tipo_servico_nome: tipoServico ? `[${tipoServico.grupo || 'Geral'}] ${tipoServico.nome}` : '',
        quantidade: 1, preco_unitario: 0, subtotal: 0,
      }]
    }));
  };

  const addComposicao = () => {
    setForm(f => ({
      ...f,
      itens: [...f.itens, {
        id: crypto.randomUUID(), tipo: 'composicao', ordem: f.itens.length + 1,
        descricao: '', quantidade: 1, preco_unitario: 0, subtotal: 0,
        sub_itens: [], preco_por_subitem: false,
      }]
    }));
  };

  const updateItem = (idx, updatedItem) => {
    updatedItem.subtotal = calcSubtotal(updatedItem);
    setForm(f => ({ ...f, itens: f.itens.map((item, i) => i === idx ? updatedItem : item) }));
  };

  const removeItem = (idx) => {
    setForm(f => ({ ...f, itens: f.itens.filter((_, i) => i !== idx) }));
  };

  // ============ MODAL CONTROLS ============
  const defaultForm = () => ({
    oportunidade_id: '', cliente_id: '', cliente_nome: '', cliente_cnpj: '', cliente_email: '', cliente_telefone: '',
    cliente_cep: '', cliente_endereco: '', cliente_numero: '', cliente_complemento: '',
    cliente_bairro: '', cliente_cidade: '', cliente_estado: '',
    entrega_usar_principal: true,
    entrega_cep: '', entrega_endereco: '', entrega_numero: '', entrega_complemento: '',
    entrega_bairro: '', entrega_cidade: '', entrega_estado: '',
    empresa_id: '', empresa_nome: '', itens: [],
    desconto_tipo: 'percentual', desconto_valor: 0,
    condicoes: { forma_pagamento: '', prazo_entrega: '', validade_dias: 30, garantia: '', frete: 'FOB' },
    texto_apresentacao: '', texto_final: '',
    endereco_entrega: '', prazo_pagamento: '', dados_bancarios: '',
    declaracao_publica: '', local_data: '', local_data_cidade: 'Curitiba-PR', local_data_date: '',
    termos_condicoes: '', kit_montagem: '',
  });

  const openNew = () => {
    setEditingProposta(null);
    setForm(defaultForm());
    setClienteSearchTerm('');
    setClienteSearchResults([]);
    setActiveTab('cliente');
    setModalOpen(true);
  };

  const openEdit = (proposta) => {
    setEditingProposta(proposta);
    const itensComOpcoes = (proposta.itens || []).map(item => {
      if (item.tipo === 'embarcacao' && item.vessel_model_id) {
        const modelo = modelos.find(m => m.id === item.vessel_model_id);
        const config = modelo?.config_comercial || {};
        return { ...item, _available_upgrades: config.upgrades || [], _available_opcionais: config.opcionais || [] };
      }
      return item;
    });
    let cidade = 'Curitiba-PR';
    if (proposta.local_data) {
      const parts = proposta.local_data.split(',');
      if (parts.length >= 2) { cidade = parts[0].trim(); }
    }
    setForm({
      oportunidade_id: proposta.oportunidade_id || '', cliente_id: proposta.cliente_id || '',
      cliente_nome: proposta.cliente_nome || '', cliente_cnpj: proposta.cliente_cnpj || '',
      cliente_email: proposta.cliente_email || '', cliente_telefone: proposta.cliente_telefone || '',
      // Endereço principal do cliente (compat com propostas antigas)
      cliente_cep: proposta.cliente_cep || '', cliente_endereco: proposta.cliente_endereco || '',
      cliente_numero: proposta.cliente_numero || '', cliente_complemento: proposta.cliente_complemento || '',
      cliente_bairro: proposta.cliente_bairro || '', cliente_cidade: proposta.cliente_cidade || '',
      cliente_estado: proposta.cliente_estado || '',
      // Endereço de entrega
      entrega_usar_principal: proposta.entrega_usar_principal !== undefined ? !!proposta.entrega_usar_principal : true,
      entrega_cep: proposta.entrega_cep || '', entrega_endereco: proposta.entrega_endereco || '',
      entrega_numero: proposta.entrega_numero || '', entrega_complemento: proposta.entrega_complemento || '',
      entrega_bairro: proposta.entrega_bairro || '', entrega_cidade: proposta.entrega_cidade || '',
      entrega_estado: proposta.entrega_estado || '',
      empresa_id: proposta.empresa_id || '', empresa_nome: proposta.empresa_nome || '',
      itens: itensComOpcoes,
      desconto_tipo: proposta.desconto_tipo || 'percentual', desconto_valor: proposta.desconto_valor || 0,
      condicoes: proposta.condicoes || { forma_pagamento: '', prazo_entrega: '', validade_dias: 30, garantia: '', frete: 'FOB' },
      texto_apresentacao: proposta.texto_apresentacao || '', texto_final: proposta.texto_final || '',
      endereco_entrega: proposta.endereco_entrega || '', prazo_pagamento: proposta.prazo_pagamento || '',
      dados_bancarios: proposta.dados_bancarios || '',
      declaracao_publica: proposta.declaracao_publica || '', local_data: proposta.local_data || '',
      local_data_cidade: cidade, local_data_date: '',
      termos_condicoes: proposta.termos_condicoes || '', kit_montagem: proposta.kit_montagem || '',
      exibir_subtotal_lote: proposta.exibir_subtotal_lote || false,
    });
    setActiveTab('cliente');
    setModalOpen(true);
  };

  const vincularOportunidade = (oportunidadeId) => {
    const op = oportunidades.find(o => o.id === oportunidadeId);
    if (!op) return;
    setForm(f => ({
      ...f, oportunidade_id: op.id,
      cliente_id: op.cliente_id || '', cliente_nome: op.cliente_nome || op.contato_nome || '',
      cliente_email: op.contato_email || '', cliente_telefone: op.contato_telefone || '',
    }));
  };

  // ============ SAVE ============
  const handleSave = async () => {
    if (!form.cliente_nome) { toast.error('Informe o nome do cliente'); setActiveTab('cliente'); return; }
    if (!form.empresa_id) { toast.error('Selecione a empresa emissora da proposta'); setActiveTab('cliente'); return; }
    if (form.itens.length === 0) { toast.error('Adicione ao menos um item à proposta'); setActiveTab('itens'); return; }
    try {
      setSaving(true);
      const cleanItens = form.itens.map(({ _available_upgrades, _available_opcionais, ...item }) => ({
        ...item, subtotal: calcSubtotal(item),
      }));
      const cidade = form.local_data_cidade || 'Curitiba-PR';
      const dataFormatada = form.local_data_date ? format(new Date(form.local_data_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : '';
      const localData = dataFormatada ? `${cidade}, ${dataFormatada}` : cidade;
      
      // Concatena endereco_entrega (legacy) a partir dos campos estruturados (retrocompat com PDF)
      const ent = form.entrega_usar_principal
        ? {
            cep: form.cliente_cep, endereco: form.cliente_endereco, numero: form.cliente_numero,
            complemento: form.cliente_complemento, bairro: form.cliente_bairro,
            cidade: form.cliente_cidade, estado: form.cliente_estado,
          }
        : {
            cep: form.entrega_cep, endereco: form.entrega_endereco, numero: form.entrega_numero,
            complemento: form.entrega_complemento, bairro: form.entrega_bairro,
            cidade: form.entrega_cidade, estado: form.entrega_estado,
          };
      const enderecoConcat = [
        ent.endereco,
        ent.numero && `nº ${ent.numero}`,
        ent.complemento,
        ent.bairro,
        ent.cidade && ent.estado ? `${ent.cidade}/${ent.estado}` : ent.cidade || ent.estado,
        ent.cep && `CEP ${ent.cep}`,
      ].filter(Boolean).join(' · ');
      
      const payload = { ...form, itens: cleanItens, local_data: localData, endereco_entrega: enderecoConcat };
      delete payload.local_data_cidade;
      delete payload.local_data_date;
      if (editingProposta) {
        await api.put(`/propostas/${editingProposta.id}`, payload);
        toast.success('Proposta atualizada');
      } else {
        await api.post('/propostas', payload);
        toast.success('Proposta criada');
      }
      setModalOpen(false);
      fetchPropostas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar proposta'); }
    finally { setSaving(false); }
  };

  const changeStatus = async (proposta, novoStatus) => {
    try {
      await api.put(`/propostas/${proposta.id}/status`, { status: novoStatus });
      toast.success(`Status alterado para ${STATUS_CONFIG[novoStatus]?.label}`);
      fetchPropostas();
      if (viewProposta?.id === proposta.id) {
        const res = await api.get(`/propostas/${proposta.id}`);
        setViewProposta(res.data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao alterar status'); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/propostas/${deleteConfirm.id}`);
      toast.success('Proposta excluída'); setDeleteConfirm(null); fetchPropostas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao excluir'); }
  };

  const downloadPdf = async (proposta) => {
    try {
      toast.info('Gerando PDF...');
      const res = await api.get(`/propostas/${proposta.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.download = `proposta_${proposta.numero}.pdf`; link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF gerado!');
    } catch { toast.error('Erro ao gerar PDF'); }
  };

  const previewPdf = async (proposta) => {
    try {
      toast.info('Gerando preview...');
      const res = await api.get(`/propostas/${proposta.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      setPdfPreview({
        url: blobUrl,
        titulo: `Proposta ${proposta.numero || ''} - ${proposta.cliente_nome || ''}`.trim(),
        blobUrl,
      });
    } catch { toast.error('Erro ao abrir preview do PDF'); }
  };

  const closePdfPreview = () => {
    if (pdfPreview?.blobUrl) window.URL.revokeObjectURL(pdfPreview.blobUrl);
    setPdfPreview(null);
  };

  // ============ FILTER ============
  const filtered = propostas.filter(p => {
    if (filtroStatus !== 'todos' && p.status !== filtroStatus) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return (p.numero || '').toLowerCase().includes(q) || (p.cliente_nome || '').toLowerCase().includes(q) || (p.vendedor_nome || '').toLowerCase().includes(q) || (p.contato_nome || '').toLowerCase().includes(q) || (p.contato_telefone || '').includes(busca);
    }
    return true;
  });

  // ============ RENDER ============
  return (
    <div data-testid="propostas-page" className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <TitleWithHelp
            title="Propostas Comerciais"
            description="Crie, gerencie e acompanhe propostas comerciais para seus clientes. Inclui geração de PDF profissional, controle de status e valor por extenso."
            connections={['CRM', 'Clientes', 'Empresas', 'Produtos']}
          >
            <h1 className="text-2xl font-bold text-slate-800">Propostas Comerciais</h1>
          </TitleWithHelp>
          <p className="text-sm text-slate-500">Gerencie e gere propostas para seus clientes</p>
        </div>
        <Button data-testid="new-proposta-btn" onClick={openNew} className="gap-2 bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4" /> Nova Proposta
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total || 0, icon: FileText, color: 'text-slate-600', help: 'Total de propostas comerciais cadastradas.' },
          { label: 'Rascunhos', value: stats.rascunhos || 0, icon: FileText, color: 'text-gray-500', help: 'Propostas ainda não enviadas ao cliente.' },
          { label: 'Enviadas', value: stats.enviadas || 0, icon: Send, color: 'text-blue-600', help: 'Propostas já enviadas e aguardando retorno do cliente.' },
          { label: 'Aceitas', value: stats.aceitas || 0, icon: CheckCircle2, color: 'text-green-600', help: 'Propostas aceitas e convertidas em negócio.' },
          { label: 'Valor Pipeline', value: formatCurrency(stats.valor_total_pipeline || 0), icon: DollarSign, color: 'text-emerald-600', wide: true, help: 'Soma de todas as propostas ativas (rascunho + enviadas) no funil.' },
        ].map((s, i) => (
          <Card key={i} className={s.wide ? 'col-span-2 md:col-span-1' : ''}>
            <CardContent className="p-3 flex items-center gap-3">
              <s.icon className={`w-5 h-5 ${s.color}`} />
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-xs text-slate-500">{s.label}</p>
                  <HelpTooltip description={s.help} iconSize={12} />
                </div>
                <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input data-testid="search-propostas" placeholder="Buscar por n, cliente ou vendedor..." value={busca}
            onChange={e => setBusca(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={filtroStatus} onValueChange={setFiltroStatus}>
          <SelectTrigger data-testid="filter-status" className="w-40 h-9"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="rascunho">Rascunho</SelectItem>
            <SelectItem value="enviada">Enviada</SelectItem>
            <SelectItem value="aceita">Aceita</SelectItem>
            <SelectItem value="recusada">Recusada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-slate-500">
            <FileText className="w-12 h-12 mb-3 text-slate-300" />
            <p className="font-medium">Nenhuma proposta encontrada</p>
            <p className="text-sm mt-1">Crie sua primeira proposta comercial</p>
          </CardContent>
        </Card>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left p-3 font-semibold text-slate-600">N</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Cliente</th>
                  <th className="text-left p-3 font-semibold text-slate-600 hidden md:table-cell">Vendedor</th>
                  <th className="text-right p-3 font-semibold text-slate-600">Valor</th>
                  <th className="text-center p-3 font-semibold text-slate-600">Status</th>
                  <th className="text-left p-3 font-semibold text-slate-600 hidden md:table-cell">Data</th>
                  <th className="text-center p-3 font-semibold text-slate-600">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const statusCfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.rascunho;
                  return (
                    <tr key={p.id} data-testid={`proposta-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="p-3 font-mono font-medium text-blue-600">
                        {p.numero}
                        {(p.versao || 1) > 1 && <Badge variant="outline" className="ml-1 text-[9px] bg-indigo-50 text-indigo-600 border-indigo-200 align-middle">v{p.versao}</Badge>}
                      </td>
                      <td className="p-3">
                        <div className="font-medium text-slate-700">{p.cliente_nome || '-'}</div>
                        {p.cliente_cnpj && <div className="text-xs text-slate-400">{p.cliente_cnpj}</div>}
                      </td>
                      <td className="p-3 text-slate-600 hidden md:table-cell">{p.vendedor_nome || '-'}</td>
                      <td className="p-3 text-right font-semibold text-slate-700">{formatCurrency(p.total)}</td>
                      <td className="p-3 text-center"><Badge variant="outline" className={`${statusCfg.cor} text-xs`}>{statusCfg.label}</Badge></td>
                      <td className="p-3 text-slate-500 text-xs hidden md:table-cell">{formatDate(p.created_at)}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Visualizar" onClick={() => setViewProposta(p)}><Eye className="w-4 h-4" /></Button>
                          {['rascunho', 'enviada', 'expirada'].includes(p.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              title={p.status === 'rascunho' ? 'Editar' : `Editar (status: ${p.status} — gera nova versão)`}
                              data-testid={`btn-editar-proposta-${p.id}`}
                              onClick={() => openEdit(p)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="PDF" onClick={() => downloadPdf(p)}><Download className="w-4 h-4 text-blue-500" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" title="Excluir" onClick={() => setDeleteConfirm(p)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ============ CREATE/EDIT MODAL ============ */}
      <Dialog open={modalOpen} onOpenChange={(open) => {
        if (!open) {
          const hasData = form.cliente_nome || form.itens?.length > 0;
          if (hasData && !window.confirm('Tem certeza? Os dados não salvos serão perdidos.')) return;
          setModalOpen(false);
        }
      }}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-lg">
              {editingProposta ? `Editar Proposta ${editingProposta.numero}` : 'Nova Proposta Comercial'}
            </DialogTitle>
          </DialogHeader>

          {/* Aviso de nova versão quando editando proposta já enviada/expirada */}
          {editingProposta && ['enviada', 'expirada'].includes(editingProposta.status) && (
            <div
              data-testid="aviso-edicao-nova-versao"
              className="flex items-start gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm"
            >
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p className="font-medium">Editando proposta {editingProposta.status === 'enviada' ? 'já enviada' : 'expirada'} (v{editingProposta.versao || 1}).</p>
                <p className="text-xs mt-0.5 text-amber-800">
                  Ao salvar, será criada a versão v{(editingProposta.versao || 1) + 1}. A versão atual fica preservada no histórico para comparação.
                </p>
              </div>
            </div>
          )}

          {/* Stepper Visual */}
          {(() => {
            const steps = [
              { key: 'cliente', label: 'Cliente', ok: !!form.cliente_nome },
              { key: 'itens', label: 'Itens', ok: form.itens.length > 0 },
              { key: 'condicoes', label: 'Forma Pgto', ok: !!form.condicoes.forma_pagamento },
              { key: 'revisao', label: 'Revisao', ok: false },
            ];
            const currentIdx = steps.findIndex(s => s.key === activeTab);
            return (
              <div data-testid="proposta-stepper" className="flex items-center gap-1 mb-2 flex-wrap sm:flex-nowrap">
                {steps.map((step, i) => {
                  const isActive = step.key === activeTab;
                  const isPast = i < currentIdx;
                  return (
                    <React.Fragment key={step.key}>
                      <button
                        onClick={() => setActiveTab(step.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                          isActive ? 'bg-blue-600 text-white shadow-sm' :
                          isPast && step.ok ? 'bg-green-50 text-green-700 border border-green-200' :
                          'bg-slate-50 text-slate-500 hover:bg-slate-100'
                        }`}>
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                          isActive ? 'bg-white/20 text-white' : isPast && step.ok ? 'bg-green-100 text-green-600' : 'bg-slate-200 text-slate-500'
                        }`}>
                          {isPast && step.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                        </span>
                        <span>{step.label}</span>
                      </button>
                      {i < steps.length - 1 && <div className={`flex-1 h-px ${i < currentIdx ? 'bg-green-300' : 'bg-slate-200'}`} />}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })()}

          {form.itens.length > 0 && activeTab !== 'revisao' && (
            <div data-testid="proposta-valor-resumo" className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-sm mb-1">
              <span className="text-slate-500">{form.itens.length} {form.itens.length === 1 ? 'item' : 'itens'}</span>
              <span className="font-bold text-slate-800">{formatCurrency(totalFinal)}</span>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="hidden">
              <TabsTrigger value="cliente">Cliente</TabsTrigger>
              <TabsTrigger value="itens">Itens</TabsTrigger>
              <TabsTrigger value="condicoes">Condições</TabsTrigger>
              <TabsTrigger value="revisao">Revisão</TabsTrigger>
            </TabsList>

            {/* ========== TAB 1: CLIENTE ========== */}
            <TabsContent value="cliente" className="space-y-4 mt-4">
              {!editingProposta && (
                <div>
                  <Label className="text-xs font-medium">Vincular a Oportunidade (opcional)</Label>
                  <Select value={form.oportunidade_id || 'none'} onValueChange={(v) => {
                    if (v === 'none') setForm(f => ({ ...f, oportunidade_id: '' }));
                    else vincularOportunidade(v);
                  }}>
                    <SelectTrigger data-testid="select-oportunidade" className="h-9"><SelectValue placeholder="Selecione uma oportunidade" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {oportunidades.map(op => (
                        <SelectItem key={op.id} value={op.id}>{op.titulo} - {op.cliente_nome || op.contato_nome || 'Sem cliente'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-xs font-medium">Buscar Cliente Cadastrado</Label>
                <p className="text-xs text-slate-500 mb-1">Digite nome, telefone, CPF, CNPJ ou email</p>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input data-testid="input-busca-cliente" value={clienteSearchTerm}
                    onChange={e => { setClienteSearchTerm(e.target.value); if (!e.target.value) setForm(f => ({ ...f, cliente_id: '', cliente_nome: '' })); }}
                    placeholder="Digite para buscar..." className="pl-9 h-9" />
                  {searchingCliente && <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin" />}
                </div>
                {clienteSearchResults.length > 0 && (
                  <div className="border rounded-lg mt-1 max-h-48 overflow-y-auto bg-white shadow-lg">
                    {clienteSearchResults.map(c => (
                      <div key={c.id} className="p-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0" onClick={() => handleSelectCliente(c)}>
                        <div className="font-medium text-sm">{c.nome}</div>
                        <div className="text-xs text-slate-500 flex gap-3">
                          {c.telefone && <span>{c.telefone}</span>}
                          {c.email && <span>{c.email}</span>}
                          {c.cnpj && <span>CNPJ: {c.cnpj}</span>}
                          {c.cpf && <span>CPF: {c.cpf}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {form.cliente_id && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                    <div>
                      <span className="text-xs text-green-600">Cliente vinculado:</span>
                      <span className="ml-2 font-medium text-sm">{form.cliente_nome}</span>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setForm(f => ({ ...f, cliente_id: '', cliente_nome: '', cliente_cnpj: '', cliente_email: '', cliente_telefone: '' })); setClienteSearchTerm(''); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nome / Razão Social *</Label>
                  <Input data-testid="input-cliente-nome" value={form.cliente_nome}
                    onChange={e => setForm(f => ({ ...f, cliente_nome: e.target.value }))} placeholder="Nome do cliente" className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">CNPJ / CPF</Label>
                  <Input value={form.cliente_cnpj} onChange={e => setForm(f => ({ ...f, cliente_cnpj: e.target.value }))} placeholder="00.000.000/0000-00" className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">E-mail</Label>
                  <Input value={form.cliente_email} onChange={e => setForm(f => ({ ...f, cliente_email: e.target.value }))} placeholder="email@empresa.com" className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">Telefone</Label>
                  <Input value={form.cliente_telefone} onChange={e => setForm(f => ({ ...f, cliente_telefone: e.target.value }))} placeholder="(00) 00000-0000" className="h-9" />
                </div>
              </div>

              {/* Endereço Principal */}
              <div className="border-t border-slate-200 pt-3 space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Endereço Principal</Label>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                  <div className="col-span-2 md:col-span-2">
                    <Label className="text-[10px] text-slate-500">CEP</Label>
                    <Input value={form.cliente_cep} onChange={e => setForm(f => ({ ...f, cliente_cep: e.target.value }))} placeholder="00000-000" className="h-8 text-xs" data-testid="input-cliente-cep" />
                  </div>
                  <div className="col-span-2 md:col-span-3">
                    <Label className="text-[10px] text-slate-500">Logradouro</Label>
                    <Input value={form.cliente_endereco} onChange={e => setForm(f => ({ ...f, cliente_endereco: e.target.value }))} placeholder="Rua/Av." className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Número</Label>
                    <Input value={form.cliente_numero} onChange={e => setForm(f => ({ ...f, cliente_numero: e.target.value }))} placeholder="Nº" className="h-8 text-xs" />
                  </div>
                  <div className="col-span-2 md:col-span-2">
                    <Label className="text-[10px] text-slate-500">Complemento</Label>
                    <Input value={form.cliente_complemento} onChange={e => setForm(f => ({ ...f, cliente_complemento: e.target.value }))} placeholder="Sala, andar..." className="h-8 text-xs" />
                  </div>
                  <div className="col-span-2 md:col-span-2">
                    <Label className="text-[10px] text-slate-500">Bairro</Label>
                    <Input value={form.cliente_bairro} onChange={e => setForm(f => ({ ...f, cliente_bairro: e.target.value }))} className="h-8 text-xs" />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Label className="text-[10px] text-slate-500">Cidade</Label>
                    <Input value={form.cliente_cidade} onChange={e => setForm(f => ({ ...f, cliente_cidade: e.target.value }))} className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">UF</Label>
                    <Input value={form.cliente_estado} onChange={e => setForm(f => ({ ...f, cliente_estado: e.target.value.toUpperCase().slice(0, 2) }))} maxLength={2} className="h-8 text-xs uppercase" />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-1">
                  <Label className="text-xs">Empresa Emissora *</Label>
                  <HelpTooltip description="Obrigatório. A empresa que emitirá a proposta. Logo, CNPJ e dados fiscais aparecerão no PDF." iconSize={12} />
                </div>
                <Select value={form.empresa_id || 'none'} onValueChange={(v) => {
                  if (v === 'none') setForm(f => ({ ...f, empresa_id: '', empresa_nome: '' }));
                  else { const emp = empresas.find(e => e.id === v); setForm(f => ({ ...f, empresa_id: v, empresa_nome: emp?.razao_social || emp?.nome || '' })); }
                }}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Selecione a empresa" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {empresas.map(e => (<SelectItem key={e.id} value={e.id}>{e.razao_social || e.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end pt-2">
                <Button type="button" onClick={() => setActiveTab('itens')} className="bg-blue-600 hover:bg-blue-700 gap-1">
                  Próximo: Itens <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                </Button>
              </div>
            </TabsContent>

            {/* ========== TAB 2: ITENS ========== */}
            <TabsContent value="itens" className="space-y-4 mt-4">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs text-slate-500 font-medium">Adicionar:</span>
                <div className="flex items-center gap-1">
                  <Select onValueChange={(modelId) => { const modelo = modelos.find(m => m.id === modelId); if (modelo) addEmbarcacao(modelo); }}>
                    <SelectTrigger data-testid="add-embarcacao-btn" className="w-auto h-8 gap-1 text-xs border-blue-200 text-blue-700">
                      <Ship className="w-3.5 h-3.5" /><span>Embarcação</span>
                    </SelectTrigger>
                    <SelectContent>
                      {modelos.map(m => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.nome} ({m.codigo}) {m.config_comercial?.preco_base ? `- ${formatCurrency(m.config_comercial.preco_base)}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <HelpTooltip title="Embarcação" description="Selecione um modelo de embarcação cadastrado. Inclui itens de série, upgrades e opcionais configurados na aba comercial." iconSize={14} />
                </div>
                <div className="flex items-center gap-1">
                  <Select onValueChange={(tsId) => { const ts = tiposServico.find(t => t.id === tsId); addServico(ts); }}>
                    <SelectTrigger data-testid="add-servico-btn" className="w-auto h-8 gap-1 text-xs border-amber-200 text-amber-700">
                      <Wrench className="w-3.5 h-3.5" /><span>Serviço</span>
                    </SelectTrigger>
                    <SelectContent>
                      {tiposServico.filter(ts => {
                        const nome = ts.nome?.toLowerCase() || '';
                        return !nome.startsWith('frete') && !nome.includes('reembolso') && !nome.includes('entrega');
                      }).map(ts => (
                        <SelectItem key={ts.id} value={ts.id}>
                          [{ts.grupo || 'Geral'}] {ts.nome}
                        </SelectItem>
                      ))}
                      <SelectItem value="__custom__">[+] Serviço personalizado</SelectItem>
                    </SelectContent>
                  </Select>
                  <HelpTooltip title="Serviço" description="Selecione um tipo de serviço cadastrado (Assistência Técnica, Consultoria, etc.) ou crie um serviço personalizado." iconSize={14} />
                </div>
                <div className="flex items-center gap-1">
                  <Button data-testid="add-composicao-btn" variant="outline" size="sm" className="h-8 text-xs gap-1 border-purple-200 text-purple-700" onClick={addComposicao}>
                    <Layers className="w-3.5 h-3.5" /> Compor Produto
                  </Button>
                  <HelpTooltip title="Compor Produto" description="Crie um produto personalizado composto por itens do catálogo. Defina um nome e adicione sub-itens com preço individual ou valor único para o produto." iconSize={14} />
                </div>
              </div>

              {/* Product search */}
              <div className="relative">
                <div className="flex items-center gap-1 mb-1">
                  <Search className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs text-slate-500">Buscar produto do catálogo</span>
                  <HelpTooltip title="Produto" description="Busque e adicione produtos avulsos do catálogo à proposta." iconSize={14} />
                  <div className="flex-1" />
                  <Button type="button" variant="outline" size="sm" className="h-6 text-[10px] px-2 gap-1 text-emerald-700 border-emerald-200"
                    onClick={() => setShowCadastroRapidoProduto(true)}>
                    <Plus className="w-3 h-3" /> Cadastro Rápido
                  </Button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input data-testid="search-produto-input" placeholder="Buscar produto do catálogo para adicionar..."
                    value={buscaProduto} onChange={e => setBuscaProduto(e.target.value)} className="pl-9 h-9" />
                  {searchingProd && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-slate-400" />}
                  {produtoResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {produtoResults.map(p => (
                        <button key={p.id} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between items-center" onClick={() => addProduto(p)}>
                          <div>
                            <span className="font-medium">{p.nome}</span>
                            {p.sku && <span className="text-xs text-slate-400 ml-2">{p.sku}</span>}
                          </div>
                          <span className="text-sm font-semibold text-emerald-600">{formatCurrency(p.preco_venda)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Lote Management */}
              {form.itens.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-indigo-500" />
                      <span className="text-xs font-semibold text-slate-600">Agrupamento por Lote</span>
                      <HelpTooltip description="Agrupe itens em lotes (ex: LOTE 1, LOTE 2). Itens sem lote aparecem avulsos no PDF. Opcional." iconSize={12} />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <Checkbox
                          checked={form.exibir_subtotal_lote || false}
                          onCheckedChange={(v) => setForm(f => ({ ...f, exibir_subtotal_lote: !!v }))}
                          data-testid="toggle-subtotal-lote"
                        />
                        <Label className="text-[10px] text-slate-500">Exibir subtotal por lote</Label>
                      </div>
                      <Button
                        type="button" variant="outline" size="sm"
                        className="h-6 text-[10px] px-2 gap-1 text-indigo-600 border-indigo-200"
                        data-testid="add-lote-btn"
                        onClick={() => {
                          const lotes = [...new Set(form.itens.map(i => i.lote).filter(Boolean))];
                          const next = `LOTE ${lotes.length + 1}`;
                          toast.info(`Lote "${next}" disponível. Atribua itens selecionando o lote em cada item.`);
                        }}
                      >
                        <Plus className="w-3 h-3" /> Novo Lote
                      </Button>
                    </div>
                  </div>
                  {/* Show existing lotes */}
                  {(() => {
                    const lotes = [...new Set(form.itens.map(i => i.lote).filter(Boolean))];
                    if (lotes.length === 0) return (
                      <p className="text-[10px] text-slate-400">Nenhum lote criado. Atribua um lote diretamente em cada item abaixo.</p>
                    );
                    return (
                      <div className="flex flex-wrap gap-1.5">
                        {lotes.map(l => {
                          const count = form.itens.filter(i => i.lote === l).length;
                          return (
                            <Badge key={l} variant="outline" className="text-[10px] bg-indigo-50 text-indigo-600 border-indigo-200">
                              {l} ({count} {count === 1 ? 'item' : 'itens'})
                            </Badge>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Items list */}
              {form.itens.length === 0 ? (
                <div className="text-center py-12 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
                  <Ship className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm font-medium">Nenhum item adicionado</p>
                  <p className="text-xs mt-1">Adicione embarcações, produtos, serviços ou composições acima</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {form.itens.map((item, idx) => (
                    item.tipo === 'embarcacao' ? (
                      <EmbarcacaoItemEditor key={item.id} item={item} index={idx} onUpdate={updateItem} onRemove={removeItem} modelos={modelos} />
                    ) : item.tipo === 'composicao' ? (
                      <div key={item.id} data-testid={`proposta-item-${idx}`} className="border border-purple-200 rounded-lg bg-white">
                        <div className="flex items-center gap-3 p-3 bg-purple-50/50 rounded-t-lg">
                          <Layers className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          <div className="flex-1">
                            <Input value={item.descricao || ''} onChange={e => updateItem(idx, { ...item, descricao: e.target.value })}
                              placeholder="Nome do produto composto" className="h-7 text-sm font-semibold border-none bg-transparent p-0 focus-visible:ring-0" />
                          </div>
                          <Input
                            value={item.lote || ''}
                            onChange={e => updateItem(idx, { ...item, lote: e.target.value.toUpperCase() })}
                            placeholder="Lote (ex: LOTE 1)"
                            className="h-6 text-[10px] w-28 text-center border-indigo-200"
                          />
                          <span className="text-sm font-bold text-purple-700">{formatCurrency(calcSubtotal(item))}</span>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => removeItem(idx)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="p-3 space-y-3">
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-[10px] text-slate-500">Qtd</Label>
                              <Input type="number" min="1" value={item.quantidade || 1} onChange={e => updateItem(idx, { ...item, quantidade: parseInt(e.target.value) || 1 })} className="h-8 text-xs" />
                            </div>
                            <div className="flex items-end gap-2 col-span-2">
                              <div className="flex items-center gap-2">
                                <Checkbox checked={item.preco_por_subitem || false}
                                  onCheckedChange={(v) => updateItem(idx, { ...item, preco_por_subitem: !!v })} />
                                <Label className="text-[10px] text-slate-500">Preço por sub-item</Label>
                              </div>
                              {!item.preco_por_subitem && (
                                <div className="flex-1">
                                  <Label className="text-[10px] text-slate-500">Preço Total</Label>
                                  <CurrencyInput value={item.preco_unitario || 0} onChange={v => updateItem(idx, { ...item, preco_unitario: v })} className="h-8 text-xs" />
                                </div>
                              )}
                            </div>
                          </div>
                          <SubItensCatalogo item={item} idx={idx} updateItem={updateItem} />
                          <div>
                            <Label className="text-[10px] text-slate-500">Especificações Técnicas (opcional)</Label>
                            <div className="quill-compact mt-0.5" data-testid={`specs-item-${idx}`}>
                              <ReactQuill theme="snow" value={item.specs_tecnicas || ''} onChange={v => updateItem(idx, { ...item, specs_tecnicas: v })}
                                modules={quillModules} formats={quillFormats} placeholder="Especificações técnicas..." />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div key={item.id} data-testid={`proposta-item-${idx}`} className={`border rounded-lg p-3 ${item.tipo === 'produto' ? 'border-emerald-200' : 'border-amber-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {item.tipo === 'produto' ? <Package className="w-4 h-4 text-emerald-500" /> : <Wrench className="w-4 h-4 text-amber-500" />}
                          <Badge variant="outline" className="text-[10px]">{item.tipo === 'produto' ? 'Produto' : 'Serviço'}</Badge>
                          {item.tipo_servico_nome && <span className="text-[10px] text-slate-400">{item.tipo_servico_nome}</span>}
                          <div className="flex-1" />
                          <Input
                            value={item.lote || ''}
                            onChange={e => updateItem(idx, { ...item, lote: e.target.value.toUpperCase() })}
                            placeholder="Lote (ex: LOTE 1)"
                            className="h-6 text-[10px] w-28 text-center border-indigo-200"
                            data-testid={`item-lote-${idx}`}
                          />
                          <span className="text-sm font-bold text-slate-700">{formatCurrency(calcSubtotal(item))}</span>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => removeItem(idx)}><X className="w-4 h-4" /></Button>
                        </div>
                        <div className="grid grid-cols-5 gap-2">
                          <div className="col-span-2">
                            <Label className="text-[10px] text-slate-500">{item.tipo === 'servico' ? 'Descrição' : 'Produto'}</Label>
                            {item.tipo === 'servico' ? (
                              <Input value={item.descricao || ''} onChange={e => updateItem(idx, { ...item, descricao: e.target.value })} placeholder="Descrição do serviço" className="h-8 text-xs" />
                            ) : (
                              <Input value={item.produto_nome || ''} readOnly className="h-8 text-xs bg-slate-50" />
                            )}
                          </div>
                          <div>
                            <Label className="text-[10px] text-slate-500">Unid.</Label>
                            <Select value={item.unidade || 'UN'} onValueChange={v => updateItem(idx, { ...item, unidade: v })}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {['UN', 'M', 'M2', 'M3', 'KG', 'L', 'CJ', 'PC', 'HR', 'VB'].map(u => (
                                  <SelectItem key={u} value={u}>{u}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px] text-slate-500">Qtd</Label>
                            <Input type="number" min="1" value={item.quantidade || 1} onChange={e => updateItem(idx, { ...item, quantidade: parseInt(e.target.value) || 1 })} className="h-8 text-xs" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-slate-500">Preço Unit.</Label>
                            <CurrencyInput value={item.preco_unitario || 0} onChange={v => updateItem(idx, { ...item, preco_unitario: v })} className="h-8 text-xs" />
                          </div>
                        </div>
                        <div className="mt-2">
                          <Label className="text-[10px] text-slate-500">Especificações Técnicas (opcional)</Label>
                          <div className="quill-compact mt-0.5" data-testid={`specs-item-${idx}`}>
                            <ReactQuill theme="snow" value={item.specs_tecnicas || ''} onChange={v => updateItem(idx, { ...item, specs_tecnicas: v })}
                              modules={quillModules} formats={quillFormats} placeholder="Especificações técnicas deste item..." />
                          </div>
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}

              {/* Totals + Discount */}
              {form.itens.length > 0 && (
                <div className="border-t border-slate-200 pt-4 space-y-3">
                  <div className="flex items-end gap-4">
                    <div className="flex-1">
                      <Label className="text-xs">Desconto</Label>
                      <Select value={form.desconto_tipo} onValueChange={v => setForm(f => ({ ...f, desconto_tipo: v }))}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentual">Percentual (%)</SelectItem>
                          <SelectItem value="valor">Valor Fixo (R$)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-40">
                      <div className="relative">
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={form.desconto_valor === 0 || form.desconto_valor === '' ? '' : String(form.desconto_valor).replace('.', ',')}
                          onChange={e => {
                            // Aceita "5", "5,00", "5.00" — converte para Number simples
                            const raw = e.target.value.replace(/[^\d.,-]/g, '').replace(',', '.');
                            const num = parseFloat(raw);
                            setForm(f => ({ ...f, desconto_valor: Number.isFinite(num) && num >= 0 ? num : 0 }));
                          }}
                          placeholder={form.desconto_tipo === 'percentual' ? 'Ex: 5 ou 5,5' : 'Ex: 1500,00'}
                          className="h-8 text-xs pr-8"
                          data-testid="input-desconto-valor"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs pointer-events-none">
                          {form.desconto_tipo === 'percentual' ? '%' : 'R$'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                    <div className="flex justify-between text-slate-600"><span>Subtotal:</span><span>{formatCurrency(totalItens)}</span></div>
                    {descontoCalc > 0 && <div className="flex justify-between text-green-600"><span>Desconto{form.desconto_tipo === 'percentual' ? ` (${form.desconto_valor}%)` : ''}:</span><span>-{formatCurrency(descontoCalc)}</span></div>}
                    <div className="flex justify-between font-bold text-lg text-slate-800 border-t border-slate-200 pt-1">
                      <span>Total:</span><span data-testid="proposta-total">{formatCurrency(totalFinal)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between pt-2">
                    <Button type="button" variant="outline" onClick={() => setActiveTab('cliente')}>Voltar</Button>
                    <Button type="button" onClick={() => setActiveTab('condicoes')} className="bg-blue-600 hover:bg-blue-700 gap-1">
                      Próximo: Condições <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ========== TAB 3: CONDIÇÕES ========== */}
            <TabsContent value="condicoes" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Forma de Pagamento</Label>
                  <Select value={form.condicoes.forma_pagamento || 'none'} onValueChange={v => setForm(f => ({ ...f, condicoes: { ...f.condicoes, forma_pagamento: v === 'none' ? '' : v } }))}>
                    <SelectTrigger data-testid="select-forma-pagamento" className="h-9"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Selecione...</SelectItem>
                      <SelectItem value="a_vista">À Vista</SelectItem>
                      <SelectItem value="pix">PIX</SelectItem>
                      <SelectItem value="boleto">Boleto</SelectItem>
                      <SelectItem value="cartao">Cartão</SelectItem>
                      <SelectItem value="parcelado">Parcelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Condição/Parcelas</Label>
                  <Input value={form.prazo_pagamento} onChange={e => setForm(f => ({ ...f, prazo_pagamento: e.target.value }))}
                    placeholder="Ex: 3x, 30/60/90, À vista" className="h-9" />
                  <p className="text-xs text-slate-400 mt-1">Digite livremente: 3x, 30/60/90 dias, etc.</p>
                </div>
                <div>
                  <Label className="text-xs">Prazo de Entrega</Label>
                  <Input value={form.condicoes.prazo_entrega} onChange={e => setForm(f => ({ ...f, condicoes: { ...f.condicoes, prazo_entrega: e.target.value } }))}
                    placeholder="Ex: 120 dias úteis" className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">Validade da Proposta (dias)</Label>
                  <Input type="number" value={form.condicoes.validade_dias} onChange={e => setForm(f => ({ ...f, condicoes: { ...f.condicoes, validade_dias: parseInt(e.target.value) || 30 } }))} className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">Garantia</Label>
                  <Input value={form.condicoes.garantia} onChange={e => setForm(f => ({ ...f, condicoes: { ...f.condicoes, garantia: e.target.value } }))} placeholder="Ex: 2 anos" className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">Frete</Label>
                  <Select value={form.condicoes.frete} onValueChange={v => setForm(f => ({ ...f, condicoes: { ...f.condicoes, frete: v } }))}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FOB">FOB - Retirada na fábrica</SelectItem>
                      <SelectItem value="CIF">CIF - Entrega no destino</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Endereço de Entrega */}
              <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-slate-700">Endereço de Entrega</Label>
                  <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!form.entrega_usar_principal}
                      onChange={e => setForm(f => ({ ...f, entrega_usar_principal: e.target.checked }))}
                      className="h-3.5 w-3.5 accent-blue-600 cursor-pointer"
                      data-testid="checkbox-entrega-usar-principal"
                    />
                    Usar endereço principal para entrega
                  </label>
                </div>
                {form.entrega_usar_principal ? (
                  <div className="text-xs text-slate-600 italic px-1 py-2">
                    {(() => {
                      const partes = [
                        form.cliente_endereco,
                        form.cliente_numero && `nº ${form.cliente_numero}`,
                        form.cliente_complemento,
                        form.cliente_bairro,
                        form.cliente_cidade && form.cliente_estado
                          ? `${form.cliente_cidade}/${form.cliente_estado}`
                          : form.cliente_cidade || form.cliente_estado,
                        form.cliente_cep && `CEP ${form.cliente_cep}`,
                      ].filter(Boolean);
                      return partes.length > 0
                        ? partes.join(' · ')
                        : 'Nenhum endereço principal preenchido. Volte na aba Cliente para preencher.';
                    })()}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                    <div className="col-span-2 md:col-span-2">
                      <Label className="text-[10px] text-slate-500">CEP</Label>
                      <Input value={form.entrega_cep} onChange={e => setForm(f => ({ ...f, entrega_cep: e.target.value }))} placeholder="00000-000" className="h-8 text-xs" data-testid="input-entrega-cep" />
                    </div>
                    <div className="col-span-2 md:col-span-3">
                      <Label className="text-[10px] text-slate-500">Logradouro</Label>
                      <Input value={form.entrega_endereco} onChange={e => setForm(f => ({ ...f, entrega_endereco: e.target.value }))} placeholder="Rua/Av." className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500">Número</Label>
                      <Input value={form.entrega_numero} onChange={e => setForm(f => ({ ...f, entrega_numero: e.target.value }))} placeholder="Nº" className="h-8 text-xs" />
                    </div>
                    <div className="col-span-2 md:col-span-2">
                      <Label className="text-[10px] text-slate-500">Complemento</Label>
                      <Input value={form.entrega_complemento} onChange={e => setForm(f => ({ ...f, entrega_complemento: e.target.value }))} placeholder="Sala, andar..." className="h-8 text-xs" />
                    </div>
                    <div className="col-span-2 md:col-span-2">
                      <Label className="text-[10px] text-slate-500">Bairro</Label>
                      <Input value={form.entrega_bairro} onChange={e => setForm(f => ({ ...f, entrega_bairro: e.target.value }))} className="h-8 text-xs" />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <Label className="text-[10px] text-slate-500">Cidade</Label>
                      <Input value={form.entrega_cidade} onChange={e => setForm(f => ({ ...f, entrega_cidade: e.target.value }))} className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500">UF</Label>
                      <Input value={form.entrega_estado} onChange={e => setForm(f => ({ ...f, entrega_estado: e.target.value.toUpperCase().slice(0, 2) }))} maxLength={2} className="h-8 text-xs uppercase" />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs">Dados Bancários para Pagamento</Label>
                <Textarea value={form.dados_bancarios} onChange={e => setForm(f => ({ ...f, dados_bancarios: e.target.value }))}
                  rows={3} placeholder="Banco, Agência, Conta, PIX..." />
              </div>
              <div className="flex justify-between pt-2">
                <Button type="button" variant="outline" onClick={() => setActiveTab('itens')}>Voltar</Button>
                <Button type="button" onClick={() => setActiveTab('revisao')} className="bg-blue-600 hover:bg-blue-700 gap-1">
                  Próximo: Revisão <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                </Button>
              </div>
            </TabsContent>

            {/* ========== TAB 4: REVISÃO ========== */}
            <TabsContent value="revisao" className="space-y-4 mt-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Texto de Apresentação</Label>
                  <div className="flex gap-1">
                    {(() => {
                      const embItem = form.itens.find(i => i.tipo === 'embarcacao');
                      const modelo = embItem ? modelos.find(m => m.id === embItem.vessel_model_id) : null;
                      const templates = [];
                      if (modelo?.config_comercial?.texto_apresentacao) {
                        templates.push({ label: `Usar de "${modelo.nome}"`, text: modelo.config_comercial.texto_apresentacao });
                      }
                      templates.push({ label: 'Padrão genérico', text: `Prezado(a) ${form.cliente_nome || 'Cliente'},\n\nTemos o prazer de apresentar nossa proposta comercial conforme solicitação. Os valores e condições estão detalhados a seguir.\n\nFicamos à disposição para esclarecimentos.` });
                      return templates.map((t, i) => (
                        <Button key={i} type="button" variant="outline" size="sm" className="h-6 text-[10px] px-2"
                          onClick={() => setForm(f => ({ ...f, texto_apresentacao: t.text }))}>
                          {t.label}
                        </Button>
                      ));
                    })()}
                  </div>
                </div>
                <Textarea value={form.texto_apresentacao} onChange={e => setForm(f => ({ ...f, texto_apresentacao: e.target.value }))}
                  rows={3} placeholder="Prezado(a), temos o prazer de apresentar..." />
              </div>

              <div>
                <Label className="text-xs">Observações Finais</Label>
                <div className="quill-compact mt-1">
                  <ReactQuill theme="snow" value={form.texto_final || ''} onChange={v => setForm(f => ({ ...f, texto_final: v }))}
                    modules={quillModules} formats={quillFormats} placeholder="Ficamos à disposição para esclarecimentos..." />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Local e Data</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={form.local_data_cidade || 'Curitiba-PR'}
                      onChange={e => setForm(f => ({ ...f, local_data_cidade: e.target.value }))}
                      placeholder="Cidade-UF" className="h-9 flex-1" />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="h-9 gap-2 text-xs min-w-[160px]">
                          <CalendarIcon className="w-4 h-4" />
                          {form.local_data_date ? format(new Date(form.local_data_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : 'Selecionar data'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent mode="single" selected={form.local_data_date ? new Date(form.local_data_date) : undefined}
                          onSelect={(d) => setForm(f => ({ ...f, local_data_date: d ? d.toISOString() : '' }))}
                          locale={ptBR} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Resultado: {(form.local_data_cidade || 'Curitiba-PR')}, {form.local_data_date ? format(new Date(form.local_data_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : 'selecione a data'}
                  </p>
                </div>
                <div>
                  {form.itens.some(i => i.specs_tecnicas) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-2">
                      <p className="text-[10px] text-blue-700 font-medium">Especificações Técnicas vinculadas a {form.itens.filter(i => i.specs_tecnicas).length} item(ns)</p>
                      <p className="text-[10px] text-blue-500">Edite as especificações na aba "Itens", dentro de cada item.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">Declaração para Órgãos Públicos (opcional)</Label>
                  <Button type="button" variant="outline" size="sm" className="text-xs h-7"
                    onClick={() => setForm(f => ({
                      ...f,
                      declaracao_publica: f.declaracao_publica ? '' : 'DECLARAMOS que nos preços cotados estão incluídas todas as despesas diretas e indiretas, frete, tributos, taxa de administração, despesas com salários, encargos sociais, trabalhistas, previdenciários, fiscais e comerciais, bem como leis sociais, leis trabalhistas, seguros, taxas e contribuições, alimentação, transporte e seguro até o destino, lucro e demais encargos de qualquer natureza necessários ao cumprimento integral do objeto deste Edital e seus anexos, nada mais sendo válido pleitear a esse título.'
                    }))}>
                    {form.declaracao_publica ? 'Remover' : 'Incluir Declaração'}
                  </Button>
                </div>
                {form.declaracao_publica && (
                  <Textarea value={form.declaracao_publica} onChange={e => setForm(f => ({ ...f, declaracao_publica: e.target.value }))}
                    rows={4} className="text-xs" />
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold">Termos e Condições (opcional)</Label>
                    {(() => {
                      const embItem = form.itens.find(i => i.tipo === 'embarcacao');
                      const modelo = embItem ? modelos.find(m => m.id === embItem.vessel_model_id) : null;
                      const tpl = modelo?.config_comercial?.termos_condicoes;
                      return tpl ? (
                        <Button type="button" variant="outline" size="sm" className="h-6 text-[10px] px-2"
                          onClick={() => setForm(f => ({ ...f, termos_condicoes: tpl }))}>
                          Usar template
                        </Button>
                      ) : null;
                    })()}
                  </div>
                  <div className="quill-compact">
                    <ReactQuill theme="snow" value={form.termos_condicoes || ''} onChange={v => setForm(f => ({ ...f, termos_condicoes: v }))}
                      modules={quillModules} formats={quillFormats} placeholder="Termos e condições gerais..." />
                  </div>
                </div>
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold">Kit Montagem (opcional)</Label>
                    {(() => {
                      const embItem = form.itens.find(i => i.tipo === 'embarcacao');
                      const modelo = embItem ? modelos.find(m => m.id === embItem.vessel_model_id) : null;
                      const tpl = modelo?.config_comercial?.kit_montagem;
                      return tpl ? (
                        <Button type="button" variant="outline" size="sm" className="h-6 text-[10px] px-2"
                          onClick={() => setForm(f => ({ ...f, kit_montagem: tpl }))}>
                          Usar template
                        </Button>
                      ) : null;
                    })()}
                  </div>
                  <div className="quill-compact">
                    <ReactQuill theme="snow" value={form.kit_montagem || ''} onChange={v => setForm(f => ({ ...f, kit_montagem: v }))}
                      modules={quillModules} formats={quillFormats} placeholder="Descrição do kit de montagem incluído..." />
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div data-testid="proposta-resumo-final" className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-200">
                <h4 className="font-semibold text-slate-700 text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> Resumo da Proposta</h4>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-white rounded-lg p-2.5 border border-slate-100">
                    <p className="text-slate-400 mb-0.5">Cliente</p>
                    <p className="font-medium text-slate-700">{form.cliente_nome || '-'}</p>
                    {form.cliente_cnpj && <p className="text-slate-400 text-[10px]">{form.cliente_cnpj}</p>}
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-slate-100">
                    <p className="text-slate-400 mb-0.5">Empresa Emissora</p>
                    <p className="font-medium text-slate-700">{form.empresa_nome || 'Não selecionada'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-slate-100">
                    <p className="text-slate-400 mb-0.5">Forma de Pagamento</p>
                    <p className="font-medium text-slate-700">
                      {(() => {
                        const map = { a_vista: 'À Vista', pix: 'PIX', boleto: 'Boleto', cartao: 'Cartão', parcelado: 'Parcelado' };
                        return map[form.condicoes.forma_pagamento] || form.condicoes.forma_pagamento || '-';
                      })()}
                      {form.prazo_pagamento ? ` | ${form.prazo_pagamento}` : ''}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-slate-100">
                    <p className="text-slate-400 mb-0.5">Prazo Entrega / Validade</p>
                    <p className="font-medium text-slate-700">{form.condicoes.prazo_entrega || '-'} | {form.condicoes.validade_dias} dias</p>
                  </div>
                  {form.endereco_entrega && (
                    <div className="bg-white rounded-lg p-2.5 border border-slate-100">
                      <p className="text-slate-400 mb-0.5">Endereço de Entrega</p>
                      <p className="font-medium text-slate-700">{form.endereco_entrega}</p>
                    </div>
                  )}
                  {form.dados_bancarios && (
                    <div className="bg-white rounded-lg p-2.5 border border-slate-100">
                      <p className="text-slate-400 mb-0.5">Dados Bancários</p>
                      <p className="font-medium text-slate-700 text-[10px]">{form.dados_bancarios}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <p className="text-xs text-slate-500 font-medium">Itens ({form.itens.length})</p>
                  {form.itens.map((item, idx) => (
                    <div key={item.id || idx} className="flex items-center justify-between text-xs bg-white rounded-lg px-2.5 py-1.5 border border-slate-100">
                      <div className="flex items-center gap-1.5">
                        {item.tipo === 'embarcacao' && <Ship className="w-3 h-3 text-blue-500" />}
                        {item.tipo === 'produto' && <Package className="w-3 h-3 text-emerald-500" />}
                        {item.tipo === 'servico' && <Wrench className="w-3 h-3 text-amber-500" />}
                        {item.tipo === 'composicao' && <Layers className="w-3 h-3 text-purple-500" />}
                        <span className="font-medium text-slate-700">{item.vessel_model_nome || item.produto_nome || item.descricao || 'Item'}</span>
                        {item.quantidade > 1 && <span className="text-slate-400">x{item.quantidade}</span>}
                        {item.tipo === 'composicao' && (item.sub_itens?.length > 0) && <span className="text-slate-400 text-[10px]">({item.sub_itens.length} sub-itens)</span>}
                      </div>
                      <span className="font-semibold text-slate-700">{formatCurrency(calcSubtotal(item))}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-slate-200 pt-2 space-y-1">
                  <div className="flex justify-between text-sm text-slate-600"><span>Subtotal:</span><span>{formatCurrency(totalItens)}</span></div>
                  {descontoCalc > 0 && <div className="flex justify-between text-sm text-green-600"><span>Desconto{form.desconto_tipo === 'percentual' ? ` (${form.desconto_valor}%)` : ''}:</span><span>-{formatCurrency(descontoCalc)}</span></div>}
                  <div className="flex justify-between font-bold text-xl text-slate-800 pt-1">
                    <span>Total:</span><span>{formatCurrency(totalFinal)}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <Button type="button" variant="outline" onClick={() => setActiveTab('condicoes')}>Voltar</Button>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => {
              const hasData = form.cliente_nome || form.itens?.length > 0;
              if (hasData && !window.confirm('Tem certeza? Os dados não salvos serão perdidos.')) return;
              setModalOpen(false);
            }}>Cancelar</Button>
            <Button data-testid="save-proposta-btn" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingProposta ? 'Atualizar Proposta' : 'Criar Proposta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ CADASTRO RÁPIDO DE PRODUTO ============ */}
      <CadastroRapidoProdutoDialog
        open={showCadastroRapidoProduto}
        onClose={() => setShowCadastroRapidoProduto(false)}
        origem="Proposta"
        testIdPrefix="quick-product-proposta"
        onSuccess={(novoProduto) => {
          addProduto({ id: novoProduto.id, nome: novoProduto.nome, sku: novoProduto.sku, preco_venda: novoProduto.preco_venda || 0, descricao_completa: novoProduto.descricao_completa, descricao: novoProduto.descricao });
        }}
      />

      {/* ============ VIEW MODAL ============ */}
      <PropostaViewModal
        viewProposta={viewProposta}
        setViewProposta={setViewProposta}
        downloadPdf={downloadPdf}
        previewPdf={previewPdf}
        changeStatus={changeStatus}
        openEdit={openEdit}
      />

      {/* ============ DELETE CONFIRM ============ */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir proposta?</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja excluir a proposta {deleteConfirm?.numero}? Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ============ PDF PREVIEW MODAL ============ */}
      <Dialog open={!!pdfPreview} onOpenChange={(open) => !open && closePdfPreview()}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col"
          data-testid="proposta-preview-dialog"
        >
          <DialogHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-sm font-medium truncate pr-4">
              {pdfPreview?.titulo || 'Preview'}
            </DialogTitle>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => pdfPreview?.url && window.open(pdfPreview.url, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Nova aba
            </Button>
          </DialogHeader>
          <div className="flex-1 bg-gray-100 overflow-hidden">
            {pdfPreview && (
              <iframe
                src={pdfPreview.url}
                className="w-full h-full border-0"
                title={pdfPreview.titulo}
                data-testid="proposta-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
